import { createFileRoute } from "@tanstack/react-router";
import { aabb2Init, aabb2AddPoint } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import Canvas2D from "lib/components/debug/Canvas2D";
import Canvas3D from "lib/components/debug/Canvas3D";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import all from "test/unit/visual/visual.fixtures";
import type { GeomTestCtx } from "test/unit/visual/visual.util";
import { runGeomTestInReact } from "test/unit/visual/visual.util";
import Input from "ui/forms/Input";
import InputLabel from "ui/forms/InputLabel";
import useQueryParams from "ui/hooks/useQueryParams";

const spacing = 200;
const buf = spacing / 5;

function GeomTests() {
  const { filter: fromURL } = useQueryParams(["filter"]);
  const [filter, setFilter] = useState<string>(fromURL ? decodeURI(fromURL) : "");
  const [testDisplaySize, setTestDisplaySize] = useState<number>(300);
  const [results, setResults] = useState<{ [key: string]: GeomTestCtx }>({});

  useEffect(() => {
    const bresults: { [key: string]: GeomTestCtx } = {};
    let num = 0;
    for (const testCase of all) {
      if (
        filter &&
        !testCase.name.toLowerCase().includes(filter.toLowerCase()) &&
        !testCase.name.toLowerCase().match(filter.toLowerCase())
      ) {
        continue;
      }
      bresults[testCase.name] = runGeomTestInReact(testCase);
      num++;
    }
    setResults(bresults);
  }, [filter, testDisplaySize]);

  return (
    <div style={{ backgroundColor: "#eeeeee", height: "100%" }}>
      <div style={{ padding: "5px", float: "left", top: "0px" }}>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter tests"
          style={{ width: "100%" }}
        />
        <InputLabel text="display size" />
        <Input
          style={{ width: "100%" }}
          type="range"
          min={10}
          max={1000}
          step={0.001}
          value={testDisplaySize}
          onChange={(e) => setTestDisplaySize(parseFloat(e.target.value))}
        />
        <div style={{ fontSize: "xx-small" }}>
          {Object.keys(results).map((id) => {
            return (
              <div key={id}>
                <a href={`?filter=${encodeURI(id)}`}>{id}</a>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ overflow: "scroll", height: "100%" }}>
        {Object.keys(results).map((id, i) => {
          const test = results[id];

          let xrange2 = 1;
          let yrange2 = 1;
          let scale2 = 3;
          let twoDee: ReactNode = null;
          let dim2 = scale2D(xrange2, yrange2, testDisplaySize) + buf * 2;
          if (test.has2D()) {
            const aabb2 = bounds2D(test);

            ({ xrange2, yrange2 } = range2D(aabb2));

            scale2 = scale2D(xrange2, yrange2, testDisplaySize);

            const range2 = Math.max(xrange2, yrange2);

            dim2 = scale2 * range2 + buf * 2;

            twoDee = (
              <Canvas2D
                width={dim2}
                height={dim2}
                text={test.text2}
                points={test.point2s}
                lines={test.line2s}
                polygons={test.polygon2s}
                offset={offset2D(aabb2, buf, scale2)}
                scale={scale2}
              />
            );
          } else {
          }

          let threeDee: ReactNode = null;
          if (test.has3D()) {
            threeDee = <Canvas3D lines={test.line3s} faces={test.faces} width={dim2} height={dim2} />;
          }

          return (
            <div
              key={id + i}
              style={{
                verticalAlign: "top",
                display: "inline-block",
                margin: "2px",
                padding: "2px",
                width: `${dim2 + 18}px`,
                border: `4px solid ${test.hadError() ? "red" : "rgba(0,0,0,0)"}`,
              }}
            >
              {twoDee}
              {threeDee}
              <a href={`?filter=${encodeURI(id)}`}>
                <div style={{ verticalAlign: "top", fontSize: `${testDisplaySize / 20}px` }}>
                  {id}
                  {test.logs().map((l, il) => (
                    <div key={`${il}`}>{l}</div>
                  ))}
                  {test.errors().length > 0 && (
                    <div style={{ color: "red", fontSize: `${testDisplaySize / 60}px` }}>
                      {test.errors().length} errors
                    </div>
                  )}
                  {test.errors().map((e, ie) => (
                    <div key={`${ie}`} style={{ color: "red", fontSize: `${testDisplaySize / 60}px` }}>
                      {e}
                    </div>
                  ))}
                  {test.warnings().map((w, iw) => (
                    <div key={`${iw}`} style={{ color: "orange", fontSize: `${testDisplaySize / 60}px` }}>
                      {w}
                    </div>
                  ))}
                  {test.warnings().length > 0 && (
                    <div style={{ color: "orange", fontSize: `${testDisplaySize / 60}px` }}>
                      {test.warnings().length} warnings
                    </div>
                  )}
                </div>
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function bounds2D(test: GeomTestCtx): AABB2 {
  const aabb2 = aabb2Init();

  // expand the aabb to include all geometry
  test.line2s.forEach((line) => {
    if (!line.pline) {
      return;
    }
    // pline is a flat list; iterate over all values and add points
    for (let i = 0; i < line.pline.length; i += 2) {
      aabb2AddPoint(aabb2, line.pline[i], line.pline[i + 1]);
    }
  });
  test.polygon2s.forEach((poly) => {
    for (const loop of poly.polygon) {
      if (!loop) {
        continue;
      }
      for (let i = 0; i < loop.length; i += 2) {
        aabb2AddPoint(aabb2, loop[i], loop[i + 1]);
      }
    }
  });
  test.point2s.forEach((p) => aabb2AddPoint(aabb2, p.x, p.y));
  test.text2.forEach((t) => aabb2AddPoint(aabb2, t.x, t.y));

  return aabb2;
}

function range2D(aabb2: AABB2): { xrange2: number; yrange2: number } {
  let xrange2 = aabb2.maxX - aabb2.minX;
  let yrange2 = aabb2.maxY - aabb2.minY;
  if (isNaN(xrange2) || xrange2 === 0) {
    xrange2 = 1;
  }
  if (isNaN(yrange2) || yrange2 === 0) {
    yrange2 = 1;
  }
  return { xrange2, yrange2 };
}

function scale2D(xrange: number, yrange: number, testDisplaySize: number): number {
  return Math.min(testDisplaySize / xrange, testDisplaySize / yrange);
}

function offset2D(aabb2: AABB2, buf: number, scale: number): [number, number] {
  return [-aabb2.minX + buf / scale, -aabb2.minY + buf / scale];
}

export const Route = createFileRoute("/debug/geom/")({
  component: GeomTests,
});
